import { AnalyticsEvents, trackEvent } from "@axqora/analytics";
import { Mail, ShieldCheck } from "lucide-react";
import { useLocation } from "react-router-dom";
import { routePath } from "../assets/routes";
import { screenshots } from "../assets/screenshots";
import { PageMeta } from "../components/PageMeta";
import { landingByLocale, supportByLocale } from "../content";
import type { Locale } from "../content/types";
import { Footer } from "../sections/Footer";
import { Header } from "../sections/Header";

type SupportPageProps = {
  locale: Locale;
};

export function SupportPage({ locale }: SupportPageProps) {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const contentOnly = query.get("embed") === "1";
  const support = supportByLocale[locale];
  const landing = landingByLocale[locale];
  const emailHref = `mailto:${support.contact.email}?subject=${encodeURIComponent(support.contact.emailSubject)}`;

  // FAQ 结构化数据：复用页面问答内容，随预渲染输出，便于搜索引擎与 AI 抽取。
  const faqJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: support.faq.items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer }
    }))
  });

  return (
    <div className="page-shell">
      <PageMeta title={support.seo.title} description={support.seo.description} locale={locale} kind="support" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} />
      {contentOnly ? null : <Header content={landing} locale={locale} page="support" />}
      <main className="support-page">
        <section className="support-hero">
          <div className="support-container">
            <div className="brand-link content-only-brand" aria-label="Prismark">
              <img src={screenshots.icon} alt="Prismark" width="28" height="28" />
              <span>Prismark</span>
            </div>
            <h1>{support.hero.title}</h1>
            <p>{support.hero.description}</p>
          </div>
        </section>

        <div className="support-container support-stack">
          <section className="support-card">
            <h2>{support.guide.title}</h2>
            {support.guide.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>

          <section className="support-section" aria-labelledby="support-faq-title">
            <h2 id="support-faq-title">{support.faq.title}</h2>
            <div className="faq-grid">
              {support.faq.items.map((item) => (
                <article className="faq-card" key={item.question}>
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="support-action-grid">
            <article className="support-action-card">
              <div className="support-icon" aria-hidden="true">
                <Mail size={22} />
              </div>
              <div>
                <h2>{support.contact.title}</h2>
                <p>{support.contact.description}</p>
                <a
                  className="support-link"
                  href={emailHref}
                  onClick={() =>
                    trackEvent(AnalyticsEvents.ContactClick, {
                      product: "prismark",
                      page: "support",
                      locale,
                      target: "support_mail"
                    })
                  }
                >
                  <span>{support.contact.emailLabel}</span>
                  {support.contact.email}
                </a>
              </div>
            </article>

            {contentOnly ? null : (
              <article className="support-action-card">
                <div className="support-icon" aria-hidden="true">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <h2>{support.relatedLinks.title}</h2>
                  <div className="support-related-links">
                    {support.relatedLinks.links.map((link) => (
                      <a
                        className="support-link"
                        href={routePath(link.href)}
                        key={link.href}
                        onClick={() =>
                          trackEvent(link.href.includes("privacy") ? AnalyticsEvents.PrivacyClick : AnalyticsEvents.ClickNav, {
                            product: "prismark",
                            page: "support",
                            locale,
                            target: link.href.includes("privacy") ? "support_privacy" : "support_terms"
                          })
                        }
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              </article>
            )}
          </section>
        </div>
      </main>
      {contentOnly ? null : <Footer content={landing.footer} locale={locale} page="support" />}
    </div>
  );
}
